import { ColumnData, Font, MergedCell } from 'api';
import { GridSelect } from 'components/FeaturedSpreadsheet/components';
import MultiSelectValueViewer from 'components/MultiSelectValueViewer';
import {
  AlphaReferenceType,
  ColumnReferenceType,
  ORDER_OF_MAGNITUDES,
  referenceColumnLegend,
  RowReferenceType,
  SpreadsheetNoteCellData,
  SpreadsheetNoteConfigCell,
  SpreadsheetNoteConfigCellMap,
  SpreadsheetNoteData,
  SpreadsheetNoteTableData,
} from 'components/SpreadsheetNotes/types';
import ExcelFormattedValueViewer from 'components/SpreadsheetNotes/utilities/ExcelFormattedValueViewer/ExcelFormattedValueViewer';
import getFillStyle from 'components/SpreadsheetNotes/utilities/GetFillStyle/getFillStyle';
import { ParserParams } from 'models/interfaces/spreadsheet-types/core-types';
import { alphabetGenerator } from 'utilities';
import { spreadsheetNoteReferenceListStyles, spreadsheetNoteYearListStyles } from './SpreadsheetNoteList.styles';
import { AttributeMappingType } from '../../../pages/Financials/financials-widget-drawer/types';
import SheetAttributeManager from '../../../services/sheet-attribute-manager/SheetAttributeManager';
import SelectValueViewer from '../../SelectValueViewer';

function getWidthStyle(isWidthValid: boolean, columnWidth: number | null | undefined) {
  const MIN_EXCEL_RECOMMENDED_PX_CONVERSION = 7.713;
  const MAX_EXCEL_RECOMMENDED_PX_CONVERSION = 8;
  let widthStyle = {};
  if (isWidthValid) {
    if (columnWidth === 0) {
      widthStyle = { display: 'none' };
    } else if (columnWidth && isWidthValid) {
      widthStyle = {
        minWidth: `${columnWidth * MIN_EXCEL_RECOMMENDED_PX_CONVERSION}px`,
        maxWidth: `${columnWidth * MAX_EXCEL_RECOMMENDED_PX_CONVERSION}px`,
        overflow: 'hidden',
      };
    }
  }
  return widthStyle;
}

const spreadsheetNoteParser = (
  params: ParserParams<ColumnData, SpreadsheetNoteData, SpreadsheetNoteTableData, SpreadsheetNoteCellData>,
  isPreview: boolean,
  mapper?: SheetAttributeManager,
  zoomLevel?: number
) => {
  const { columns, rowConfig, initialObject, tableData } = params;
  const alphabet = alphabetGenerator([], columns.length);
  const mergedCells =
    initialObject?.content?.merged_cells?.reduce<Record<string, MergedCell>>((map, info) => {
      // eslint-disable-next-line no-param-reassign
      map[info.top_left] = info;
      return map;
    }, {}) || ({} as Record<string, MergedCell>);

  const attributeReferenceConfig = tableData?.attributeReferenceConfig;
  const isReferenceSheet = Boolean(tableData?.isAttributeMapperEnabled && attributeReferenceConfig);
  let iterableRowConfig = [...rowConfig];
  let iterableColumns = [...columns];
  let cells = {};
  const isMultiCell = attributeReferenceConfig?.selectionMode !== AttributeMappingType.SINGLE_CELL;

  if (isReferenceSheet && isMultiCell) {
    iterableRowConfig = rowConfig.slice(1);
    iterableColumns = columns.slice(1);

    const widthStyle = {
      minWidth: '175px',
    };

    const indicatorForSheet = mapper?.sheetAttributeMapping.sheetIndicatorMapping.find(
      item => item.sheet === initialObject?.name && item.documentReferenceId === tableData?.documentId
    );

    cells = {
      [`${referenceColumnLegend}0`]: {
        key: `${referenceColumnLegend}0`,
        rowNumber: 0,
        columnLegend: referenceColumnLegend,
        valueViewer: props => <SelectValueViewer options={ORDER_OF_MAGNITUDES} {...props} />,
        dataEditor: props => (
          <GridSelect
            extraStyles={spreadsheetNoteYearListStyles(zoomLevel)}
            menuPosition="fixed"
            options={ORDER_OF_MAGNITUDES}
            usePortal
            {...props}
          />
        ),
        readOnly: false,
        value: indicatorForSheet?.orderOfMagnitude ?? ORDER_OF_MAGNITUDES[0].value,
        style: widthStyle,
        data: {
          referenceType: AlphaReferenceType,
          sheetName: initialObject?.name || '',
        },
      },
    };
  }

  const columnCells = iterableColumns.reduce<SpreadsheetNoteConfigCellMap>((acc, column, columnIndex) => {
    const colLegend = alphabet[columnIndex];
    const columnWidth = initialObject?.content?.column_data?.[columnIndex]?.column_width;
    const isWidthValid = typeof columnWidth === 'number';

    let widthStyle = {};
    widthStyle = getWidthStyle(isWidthValid, columnWidth);
    if (isReferenceSheet) {
      const selectedColumn = mapper?.sheetAttributeMapping.sheetIndicatorMapping
        .filter(
          item =>
            item.gridIndicators.columns[colLegend] &&
            item.sheet === tableData?.sheetName &&
            item.documentReferenceId === tableData?.documentId
        )
        .map(item => item.gridIndicators.columns[colLegend].map(item => item.value))
        .flat();

      const rowNumber = 0;
      const referenceColumnLegend = `${colLegend}`;
      const key = `${referenceColumnLegend}${rowNumber}`;
      const baseOptions = attributeReferenceConfig?.columns || [];
      
      // Enrich options with ordinate metadata for AI suggestion chips
      // Pass the column key to get metadata specific to this column
      const options = baseOptions.map(option => {
        const ordinateMetadata = mapper?.getOrdinateMetadata(
          option.value as string,
          colLegend,
          'column',
          tableData?.sheetName,
          tableData?.documentId
        );
        return {
          ...option,
          ordinate: ordinateMetadata
        };
      });
      
      const cell: SpreadsheetNoteConfigCell = {
        key,
        rowNumber,
        columnLegend: referenceColumnLegend,
        valueViewer: props => (isMultiCell ? <MultiSelectValueViewer options={options} useLabel={false} useAliasList={false} {...props} /> : null),
        dataEditor: props =>
          isMultiCell ? (
            <GridSelect
              extraStyles={spreadsheetNoteYearListStyles(zoomLevel ?? 1)}
              isMulti
              matchFrom="any"
              menuPosition="fixed"
              options={options}
              usePortal
              {...props}
            />
          ) : null,
        readOnly: false,
        value: selectedColumn,
        style: widthStyle,
        data: {
          referenceType: ColumnReferenceType,
          sheetName: initialObject?.name || '',
        },
      };
      acc[key] = cell;
    }
    iterableRowConfig.forEach((row, rowIndex) => {
      const rowNumber = rowIndex + 1;
      const key = `${colLegend}${rowNumber}`;
      let value = '';
      let expr = '';
      let format = '';

      const fillStyle = getFillStyle(initialObject?.content?.cells?.[key]?.fill);

      const style = {
        ...fillStyle,
        ...widthStyle,
      };

      let font: Font = {};
      let alignment = null;
      let dataType = '';
      let rowSpan;
      let colSpan;
      if (mergedCells[key]) {
        rowSpan = mergedCells[key].row_span;
        colSpan = mergedCells[key].col_span;
      }
      if (initialObject?.content?.cells?.[key]) {
        value = initialObject.content.cells[key].value || '';
        expr = initialObject.content.cells[key].expr || '';
        format = initialObject.content.cells[key].format ?? '';
        font = initialObject.content.cells[key].font || {};
        dataType = initialObject.content.cells[key].data_type || '';
        alignment = initialObject.content.cells[key].alignment ?? null;
      }
      const readOnly = isPreview;
      const cell: SpreadsheetNoteConfigCell = {
        alias: '',
        columnLegend: '',
        expr,
        key,
        colSpan,
        rowSpan,
        style,
        rowNumber,
        value,
        readOnly,
        //   we should probably combine these into one excel property
        excelProps: {
          format,
          font,
          dataType,
          alignment,
        },
        useValueIfInvalidExpr: true,
        valueViewer: ExcelFormattedValueViewer,
      };

      acc[key] = {
        ...cell,
      };
    });
    return acc;
  }, {});

  cells = {
    ...cells,
    ...columnCells,
  };

  if (isReferenceSheet) {
    const widthStyle = {
      minWidth: '175px',
    };
    iterableRowConfig.forEach((row, rowIndex) => {
      // rows - TODO validate it is on the right sheet
      const selectedRows = mapper?.sheetAttributeMapping.sheetIndicatorMapping
        .filter(
          item =>
            item.gridIndicators.rows[rowIndex] &&
            item.sheet === tableData?.sheetName &&
            item.documentReferenceId === tableData?.documentId
        )
        .map(item => item.gridIndicators.rows[rowIndex].map(item => item.value))
        .flat();
      const rowNumber = rowIndex;
      const key = `${referenceColumnLegend}${rowNumber}`;
      if (rowIndex === 0) {
        return;
      }

      const baseOptions = attributeReferenceConfig?.rows || [];
      
      // Enrich options with ordinate metadata for AI suggestion chips
      // Pass the row index to get metadata specific to this row
      const options = baseOptions.map(option => {
        const ordinateMetadata = mapper?.getOrdinateMetadata(
          option.value as string,
          rowIndex.toString(),
          'row',
          tableData?.sheetName,
          tableData?.documentId
        );
        return {
          ...option,
          ordinate: ordinateMetadata
        };
      });

      const cell: SpreadsheetNoteConfigCell = {
        key,
        rowNumber,
        columnLegend: referenceColumnLegend,
        valueViewer: props => (isMultiCell ? <MultiSelectValueViewer options={options} useLabel={false} useAliasList={false} {...props} /> : null),
        dataEditor: props =>
          isMultiCell ? (
            <GridSelect
              extraStyles={spreadsheetNoteReferenceListStyles(zoomLevel)}
              isMulti
              matchFrom="any"
              menuPosition="fixed"
              options={options}
              usePortal
              {...props}
            />
          ) : null,
        readOnly: false,
        value: selectedRows,
        style: widthStyle,
        data: {
          referenceType: RowReferenceType,
          sheetName: initialObject?.name || '',
        },
      };
      cells[key] = cell;
    });
  }
  return cells;
};

export default spreadsheetNoteParser;
